import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Goal {
  id: string;
  user_id: string;
  name: string;
  type: 'simple_savings' | 'emergency_fund' | 'category_limit';
  target_amount: number;
  current_amount: number;
  target_date: string | null;
  category_id: string | null;
  auto_transfer: boolean;
  auto_transfer_amount: number | null;
  auto_transfer_percentage: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  category?: {
    id: string;
    name: string;
    icon: string;
    color: string;
  };
}

export type GoalType = Goal['type'];

export function useGoals() {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('goals')
        .select(`
          *,
          category:categories(id, name, icon, color)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const typedGoals = (data || []).map(goal => ({
        ...goal,
        type: goal.type as GoalType,
      })) as Goal[];

      setGoals(typedGoals);
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar cofrinhos',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createGoal = async (goal: Partial<Goal>): Promise<Goal | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data, error } = await supabase
        .from('goals')
        .insert({
          user_id: user.id,
          name: goal.name || 'Novo Cofrinho',
          type: goal.type || 'simple_savings',
          target_amount: goal.target_amount || 0,
          current_amount: goal.current_amount || 0,
          target_date: goal.target_date,
          category_id: goal.category_id,
          auto_transfer: goal.auto_transfer || false,
          auto_transfer_amount: goal.auto_transfer_amount,
          auto_transfer_percentage: goal.auto_transfer_percentage,
        })
        .select(`
          *,
          category:categories(id, name, icon, color)
        `)
        .single();

      if (error) throw error;

      const typedGoal = {
        ...data,
        type: data.type as GoalType,
      } as Goal;

      await fetchGoals();
      toast({ title: 'Cofrinho criado com sucesso!' });
      return typedGoal;
    } catch (error: any) {
      toast({
        title: 'Erro ao criar cofrinho',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateGoal = async (id: string, updates: Partial<Goal>) => {
    try {
      const { error } = await supabase
        .from('goals')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      await fetchGoals();
      toast({ title: 'Cofrinho atualizado!' });
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar cofrinho',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const deleteGoal = async (id: string) => {
    try {
      const { error } = await supabase
        .from('goals')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await fetchGoals();
      toast({ title: 'Cofrinho removido!' });
    } catch (error: any) {
      toast({
        title: 'Erro ao remover cofrinho',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const addToGoal = async (id: string, amount: number, sourceAccountId: string) => {
    try {
      const goal = goals.find(g => g.id === id);
      if (!goal) throw new Error('Cofrinho não encontrado');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Fixed UUID for system transfer expense category (set by migration 20260211200000)
      const expenseCatId = '00000000-0000-0000-0000-000000000101';

      const today = new Date().toISOString().split('T')[0];

      const { error: txError } = await supabase.from('transactions').insert({
        user_id: user.id,
        account_id: sourceAccountId,
        category_id: expenseCatId,
        amount,
        type: 'expense',
        description: `Depósito: ${goal.name}`,
        transaction_date: today,
        is_paid: true,
      });

      if (txError) throw txError;

      const newAmount = goal.current_amount + amount;
      await updateGoal(id, { current_amount: newAmount });

      toast({ title: `${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount)} depositado em ${goal.name}!` });
    } catch (error: any) {
      toast({ title: 'Erro ao depositar', description: error.message, variant: 'destructive' });
    }
  };

  const withdrawFromGoal = async (id: string, amount: number, destAccountId: string) => {
    try {
      const goal = goals.find(g => g.id === id);
      if (!goal) throw new Error('Cofrinho não encontrado');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Fixed UUID for system transfer income category (set by migration 20260211200000)
      const incomeCatId = '00000000-0000-0000-0000-000000000102';

      const today = new Date().toISOString().split('T')[0];

      const { error: txError } = await supabase.from('transactions').insert({
        user_id: user.id,
        account_id: destAccountId,
        category_id: incomeCatId,
        amount,
        type: 'income',
        description: `Resgate: ${goal.name}`,
        transaction_date: today,
        is_paid: true,
      });

      if (txError) throw txError;

      const newAmount = Math.max(0, goal.current_amount - amount);
      await updateGoal(id, { current_amount: newAmount });

      toast({ title: `${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount)} resgatado de ${goal.name}!` });
    } catch (error: any) {
      toast({ title: 'Erro ao sacar', description: error.message, variant: 'destructive' });
    }
  };

  useEffect(() => {
    fetchGoals();
  }, []);

  return {
    goals,
    loading,
    createGoal,
    updateGoal,
    deleteGoal,
    addToGoal,
    withdrawFromGoal,
    refetch: fetchGoals,
  };
}
